import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { SimcardService } from '../../../services/simcards/Simcards';
import { patchSimcard } from '../../../state/simdata/SimcardsSlice';
import { addToast } from '../../../state/toast/ToastSlice';

import { SectionCard } from '@components';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../components/buttons/LoadingSpinner';
import InputField from '../../../components/inputs/text/InputField';
import Tabs from '../../../components/tabs/Tabs';
import StatusTag from '../../../components/tags/StatusTag';
import Typography from '../../../components/typography/Typography';
import RatePlanInfo from '../../../components/typography/sim/RatePlanInfo';
import UserSelect from '../../../components/users/UserSelect';

import { useCompany } from '@hooks';
import { SimcardInterface } from '@interfaces';
import { Divider } from 'antd';
import SimActions from './SimActions';
import SimTickets from './SimTickets';
import SimUsagePerUser from './SimUsagePerUser';
import SimUserUsage from './SimUserUsage';

interface SimDetailContainerProps {
  data: any;
}

const simcardService = new SimcardService();

const SimDetailContainer = ({ data }: SimDetailContainerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { companyId } = useCompany();

  const [sim, setSim] = useState<SimcardInterface>(data.sim);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSim(data.sim);
    console.log(data.sim);
  }, [data.sim]);

  if (!sim) return <LoadingSpinner />;

  const updateSim = (props: Record<string, any>) => {
    dispatch(patchSimcard({ props, simId: sim.sim_id }));
  };

  const handleInputBlur = async (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (sim[name] === value) return;

    setSim((prev) => ({ ...prev, [name]: value }));
    updateSim({ [name]: value });

    await simcardService.updateSimcard(
      sim.sim_id,
      { [name]: value },
      companyId
    );

    dispatch(
      addToast({
        description: 'Het veld is aangepast.',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  const handleUserSelect = async (userId: string, option: any) => {
    await simcardService.updateOrCreateProximusSim(
      {
        sim_unique_nr: sim.sim_unique_nr,
        sim_user_id: userId,
      },
      companyId
    );

    updateSim({
      sim_user_id: userId,
      sim_user: option.label,
    });

    dispatch(
      addToast({
        description: 'De gebruiker is gewijzigd.',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  const handleRemoveUser = async () => {
    setLoading(true);

    try {
      await simcardService.updateOrCreateProximusSim(
        {
          sim_unique_nr: sim.sim_unique_nr,
          sim_user_id: null,
        },
        companyId
      );

      updateSim({
        sim_user_id: null,
        sim_user: null,
      });

      dispatch(
        addToast({
          description: 'Gebruiker verwijderd van de simkaart.',
          position: 'bottomRight',
          style: 'success',
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ----------------------
  // 🔹 Tabs
  // ----------------------

  const tabs = [
    {
      nav: t('Acties'),
      component: <SimActions simData={sim} setSim={setSim} />,
    },
    {
      nav: t('Tickets'),
      component: <SimTickets simId={sim.sim_id} />,
    },
    {
      nav: t('Maandverbruik'),
      component: <SimUserUsage phoneNumbers={[sim.sim_gsm_nr]} />,
    },
    {
      nav: t('Verbruik 12m'),
      component: <SimUsagePerUser props={{ phoneNumbers: [sim.sim_gsm_nr] }} />,
    },
  ];

  // ----------------------
  // 🔹 UI
  // ----------------------

  return (
    <div>
      {/* Header */}
      <SectionCard title="Simkaart">
        {/* 🔹 HEADER TOP */}
        <div className=" px-5">
          <StatusTag label={sim.sim_isActive} />
        </div>

        <div className="grid grid-cols-2 px-5 py-2 items-start gap-6">
          {/* LEFT */}
          <div className="flex flex-col">
            <div className="flex items-center">
              <u>
                <Typography tag="label" type="small">
                  Simkaart
                </Typography>
              </u>
            </div>

            <Typography tag="span" type="semibold">
              {sim.sim_unique_nr ?? 'eSim'}
            </Typography>

            <Typography tag="span" type="small" color="default">
              {sim.sim_gsm_nr ?? '/'}
            </Typography>
          </div>

          {/* RIGHT */}
          <div className="flex flex-col">
            <u>
              <Typography tag="label" type="small">
                Tariefplan
              </Typography>
            </u>

            {sim.sim_trf_id ? (
              <>
                <Typography tag="span" type="semibold">
                  {sim.tariefplan?.trf_naam}
                </Typography>

                <RatePlanInfo ratePlan={sim.tariefplan} />
                {sim.sim_options?.map((option: any) => (
                  <div className="mt-2">
                    <Typography tag="label" type="semibold">
                      +{option.trf_naam}
                    </Typography>
                    <RatePlanInfo ratePlan={option} />
                  </div>
                ))}
              </>
            ) : (
              <Typography tag="span" type="small">
                Geen tariefplan
              </Typography>
            )}
          </div>
        </div>

        <Divider />

        <div className="grid grid-cols-2 gap-6 px-5 py-2">
          {/* Gebruiker */}
          <div className="flex flex-col gap-1">
            <Typography tag="label" type="label">
              Gebruiker
            </Typography>

            <div className="flex gap-2 items-center">
              <UserSelect
                handleSelect={handleUserSelect}
                defaultValue={sim.sim_user_id ?? 'Selecteer gebruiker'}
              />
            </div>
          </div>

          {/* Afdeling */}
          <div className="flex flex-col gap-1">
            <Typography tag="label" type="label">
              Afdeling
            </Typography>

            <InputField
              type="text"
              name="sim_label"
              value={sim.sim_label ?? ''}
              onBlur={handleInputBlur}
              placeholder="Afdeling"
            />
          </div>

          {/* Nota (full width) */}
          <div className="col-span-2 flex flex-col gap-1">
            <Typography tag="label" type="label">
              Nota
            </Typography>

            <InputField
              type="text"
              name="sim_nota"
              value={sim.sim_nota ?? ''}
              onBlur={handleInputBlur}
              placeholder="Nota voor simkaart"
            />
          </div>
        </div>
      </SectionCard>

      {/* Tabs */}
      <div className="mt-5">
        {sim.sim_unique_nr ? (
          <Tabs tabs={tabs} />
        ) : (
          <p className="mt-3 opacity-80">
            Sim acties zullen hier beschikbaar zijn wanneer de simkaartnummer
            gekoppeld is.
          </p>
        )}
      </div>
    </div>
  );
};

export default SimDetailContainer;
